import * as React from 'react';
import { Suspense } from 'react';

import useRouter from 'js/lib/useRouter';
import { get as getUser, isAuthenticatedUser } from 'js/lib/user';

import BypassLogin from 'bundles/enterprise-legacy-learner-home/components/BypassLogin';
import { isForcedSSOLoginFlowEnabled } from 'bundles/saml-sso/epicFeatureFlags';
import {
  useAcceptanceNoticeLogs,
  useRefetchAcceptanceNoticeLogs,
  useRequirePreJoinCheck,
} from 'bundles/unified-common/contexts/org-scoped/EnterpriseOrgUserExperienceContext';
import {
  useOrgSlug,
  useThirdPartyOrganization,
} from 'bundles/unified-common/contexts/org-scoped/ThirdPartyOrganizationContext';

const OrgScopedPreJoinCheck = React.lazy(() =>
  import('bundles/unified-home-common/components/enterprise/OrgScopedPreJoinCheck').then((module) => ({
    default: module.OrgScopedPreJoinCheck,
  }))
);

const ATTEMPT_SSO_LOGIN = 'attemptSSOLogin';

type Props = {
  children: React.ReactNode;
};

export const EnterpriseOrgScopedUserStateCheckWrapper = ({ children }: Props) => {
  const router = useRouter();
  const thirdPartyOrganization = useThirdPartyOrganization();
  const orgSlug = useOrgSlug();
  const acceptanceNoticeLogs = useAcceptanceNoticeLogs() || [];
  const refetchAcceptanceNoticeLogs = useRefetchAcceptanceNoticeLogs();
  const requirePreJoinCheck = useRequirePreJoinCheck();

  const isUserAuthenticated = isAuthenticatedUser();
  const shouldAttemptSsoLogin = router.location.query[ATTEMPT_SSO_LOGIN] === 'true';

  // SSO bypass login for org-level authentication
  if (shouldAttemptSsoLogin && !isUserAuthenticated && thirdPartyOrganization?.id) {
    return <BypassLogin thirdPartyOrganizationId={thirdPartyOrganization.id} />;
  }

  // Privacy notice pre-join check for org-scoped pages
  if (
    thirdPartyOrganization &&
    requirePreJoinCheck &&
    acceptanceNoticeLogs.length === 0 &&
    !isForcedSSOLoginFlowEnabled()
  ) {
    return (
      // To prevent the large CML bundle from blocking initial page load
      <Suspense fallback={null}>
        <OrgScopedPreJoinCheck
          thirdPartyOrganizationId={thirdPartyOrganization.id}
          thirdPartyOrganization={thirdPartyOrganization}
          thirdPartyOrganizationSlug={orgSlug}
          userId={getUser()?.id}
          refetchAcceptanceNoticeLogs={refetchAcceptanceNoticeLogs}
        />
      </Suspense>
    );
  }

  return <React.Fragment>{children}</React.Fragment>;
};
